'use client';
import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks';
import { setModalName, setModalVisible } from '@/lib/slices/modal-slice';
import GoogleLoginButton from './(auth)/components/GoogleLoginButton';
import MicrosoftLoginButton from './(auth)/components/MicrosoftLoginButton';
import data from '@/data/en.json';
import TextModal from '@/components/modals/general/TextModal';
import { useConfig } from '@/contexts/ConfigContext';
import BrandLogo from '@/components/BrandLogo';

export default function HomePage(): React.JSX.Element {
  const router = useRouter();
  const store = useAppStore();
  const dispatch = useAppDispatch();
  const loggedIn = useAppSelector(() => store.getState().isLoggedIn.status);
  const configContext = useConfig();
  const activeConfig = configContext?.config;
  const brandName = activeConfig?.name ?? 'Assistiv';
  const brandLogo = activeConfig?.logo;
  const brandColors = activeConfig?.colors;
  const [modalData, setModalData] = useState<{
    title: string;
    version?: string;
    intro?: string;
    content: string;
    sections?: { heading: string; body: string }[];
  }>({ title: '', content: '' });

  useEffect(() => {
    if (loggedIn) {
      router.replace('/student');
    }
  }, [loggedIn, router]);

  const openModal = (isTOS: boolean): void => {
    setModalData(isTOS ? data.termsOfUse : data.privacyPolicy);
    dispatch(setModalVisible(true));
    dispatch(setModalName('TermsPolicy'));
  };

  const renderLoginLogo = (): React.ReactNode => {
    return (
      <BrandLogo
        logo={brandLogo}
        brandName={brandName}
        className='inline-flex h-24 w-auto items-center justify-center text-text_gray_900 [&>svg]:h-full [&>svg]:w-auto'
        style={{
          maxHeight: 120,
          maxWidth: 280,
          width: 'auto',
          height: 'auto',
          objectFit: 'contain',
        }}
      />
    );
  };

  const authPanelBackground = brandColors
    ? `linear-gradient(137deg, ${brandColors.brand_primary} 0%, ${brandColors.brand_auth_gradient_mid} 62%, ${brandColors.brand_auth_gradient_end} 128.02%)`
    : 'linear-gradient(137deg, rgba(0, 88, 188, 0.90) 0%, rgba(0, 160, 53, 0.90) 128.02%)';

  return (
    <div
      className='auth-page absolute left-0 top-0 w-full h-[100vh] flex flex-col md:flex-row items-center overflow-hidden'
      style={{ backgroundColor: '#FFFFFF' }}
    >
      <div className='auth-mobile-background' aria-hidden>
        <div className='auth-mobile-wave' />
      </div>

      {/* Left panel */}
      <div
        className='auth-left-panel hidden md:flex h-full flex-shrink-0 items-center justify-center'
        style={{
          width: '44vw',
          minWidth: '420px',
          backgroundColor: '#FFFFFF',
          padding: '12px',
          overflow: 'hidden',
        }}
      >
        {/* Left panel */}
        <div
          className='relative flex flex-col text-white overflow-hidden'
          style={{
            width: '100%',
            maxWidth: '760px',
            height: '100%',
            borderRadius: 20,
            background: authPanelBackground,
            padding: '35px 32px 33px 32px',
          }}
        >
          {/* Wave overlay */}
          <div
            aria-hidden
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: 20,
              backgroundImage: 'url(/login/wave.png)',
              backgroundPosition: 'center center',
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
              opacity: 3,
              pointerEvents: 'none',
              mixBlendMode: 'hard-light',
            }}
          />
          {/* Top spacer */}
          {/* <div style={{ height: '35%' }} /> */}

          {/* Content block */}
          <div
            style={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 24,
              paddingLeft: 30,
              marginTop: 40,
            }}
          >
            <h3
              className='font-bold text-[18px] md:text-[20px] lg:text-[24px] xl:text-[28px] 2xl:text-[32px] leading-snug text-white'
              style={{ transform: 'translateY(82px)' }}
            >
              Transform Your Workforce with
              <br />
              AI-Powered Learning
            </h3>

            {/* Feature List */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 30,
                transform: 'translateY(82px)',
              }}
            >
              {/* AI Course Generator */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  gap: 16,
                }}
              >
                <span
                  style={{
                    borderRadius: 15.033,
                    background: 'rgba(255, 255, 255, 0.15)',
                    padding: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='24'
                    height='24'
                    viewBox='0 0 24 24'
                    fill='none'
                  >
                    <g clipPath='url(#clip_ai_course)'>
                      <path
                        d='M9.93718 15.5002C9.84791 15.1542 9.66752 14.8384 9.4148 14.5856C9.16208 14.3329 8.84626 14.1525 8.50018 14.0632L2.36518
                    12.4812C2.26052 12.4515 2.16839 12.3885 2.1028 12.3017C2.0372 12.2149 2.00171 12.1091 2.00171 12.0002C2.00171 11.8914
                    2.0372 11.7856 2.1028 11.6988C2.16839 11.612 2.26052 11.549 2.36518 11.5192L8.50018 9.93625C8.84613 9.84705 9.16188
                    9.66682 9.41459 9.41429C9.6673 9.16175 9.84775 8.84614 9.93718 8.50025L11.5192 2.36525C11.5486 2.26017 11.6116 2.16759
                    11.6985 2.10164C11.7854 2.0357 11.8916 2 12.0007 2C12.1098 2 12.2159 2.0357 12.3029 2.10164C12.3898 2.16759 12.4528
                    2.26017 12.4822 2.36525L14.0632 8.50025C14.1525 8.84632 14.3328 9.16214 14.5856 9.41487C14.8383 9.66759 15.1541 9.84797
                    15.5002 9.93725L21.6352 11.5182C21.7407 11.5473 21.8337 11.6103 21.9 11.6973C21.9663 11.7844 22.0022 11.8908 22.0022
                    12.0002C22.0022 12.1097 21.9663 12.2161 21.9 12.3032C21.8337 12.3902 21.7407 12.4531 21.6352 12.4822L15.5002 14.0632C15.1541
                    14.1525 14.8383 14.3329 14.5856 14.5856C14.3328 14.8384 14.1525 15.1542 14.0632 15.5002L12.4812 21.6352C12.4518 21.7403
                    12.3888 21.8329 12.3019 21.8989C12.2149 21.9648 12.1088 22.0005 11.9997 22.0005C11.8906 22.0005 11.7844 21.9648 11.6975
                    21.8989C11.6106 21.8329 11.5476 21.7403 11.5182 21.6352L9.93718 15.5002Z'
                        stroke='white'
                        strokeWidth='2.50546'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                      <path
                        d='M20.0003 3V7'
                        stroke='white'
                        strokeWidth='2.50546'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                      <path
                        d='M22.0001 5H18.0001'
                        stroke='white'
                        strokeWidth='2.50546'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                      <path
                        d='M4.00009 17V19'
                        stroke='white'
                        strokeWidth='2.50546'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                      <path
                        d='M5.00018 18H3.00018'
                        stroke='white'
                        strokeWidth='2.50546'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </g>
                    <defs>
                      <clipPath id='clip_ai_course'>
                        <rect width='24' height='24' fill='white' />
                      </clipPath>
                    </defs>
                  </svg>
                </span>

                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                  }}
                >
                  <span
                    style={{
                      color: '#FFFFFF',
                      fontWeight: 600,
                      fontSize: 18,
                    }}
                  >
                    AI Course Generator
                  </span>
                  <span
                    style={{
                      color: '#FFFFFFCC',
                      fontSize: 16,
                      lineHeight: '1.5',
                    }}
                  >
                    Transform docs, policies, or transcripts into complete
                    <br />
                    courses instantly
                  </span>
                </div>
              </div>

              {/* Smart Flashcards */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  gap: 16,
                }}
              >
                <span
                  style={{
                    borderRadius: 15.033,
                    background: 'rgba(255, 255, 255, 0.15)',
                    padding: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='24'
                    height='24'
                    viewBox='0 0 24 24'
                    fill='none'
                  >
                    <path
                      d='M12.0001 7V21'
                      stroke='white'
                      strokeWidth='2.50546'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                    <path
                      d='M3.00027 18C2.73506 18 2.4807 17.8946 2.29317 17.7071C2.10563 17.5196 2.00027 17.2652 2.00027 17V4C2.00027 3.73478
                  2.10563 3.48043 2.29317 3.29289C2.4807 3.10536 2.73506 3 3.00027 3H8.00027C9.06114 3 10.0786 3.42143 10.8287 4.17157C11.5788
                  4.92172 12.0003 5.93913 12.0003 7C12.0003 5.93913 12.4217 4.92172 13.1718 4.17157C13.922 3.42143 14.9394 3 16.0003 3H21.0003C21.2655
                  3 21.5198 3.10536 21.7074 3.29289C21.8949 3.48043 22.0003 3.73478 22.0003 4V17C22.0003 17.2652 21.8949 17.5196 21.7074
                  17.7071C21.5198 17.8946 21.2655 18 21.0003 18H15.0003C14.2046 18 13.4416 18.3161 12.879 18.8787C12.3163 19.4413 12.0003
                  20.2044 12.0003 21C12.0003 20.2044 11.6842 19.4413 11.1216 18.8787C10.559 18.3161 9.79592 18 9.00027 18H3.00027Z'
                      stroke='white'
                      strokeWidth='2.50546'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </span>

                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                  }}
                >
                  <span
                    style={{
                      color: '#FFFFFF',
                      fontWeight: 600,
                      fontSize: 18,
                    }}
                  >
                    Smart Flashcards
                  </span>
                  <span
                    style={{
                      color: '#FFFFFFCC',
                      fontSize: 16,
                      lineHeight: '1.5',
                    }}
                  >
                    AI extracts key concepts and creates study materials
                    <br />
                    automatically
                  </span>
                </div>
              </div>

              {/* Adaptive Quizzes */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  gap: 16,
                }}
              >
                <span
                  style={{
                    borderRadius: 15.033,
                    background: 'rgba(255, 255, 255, 0.15)',
                    padding: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='28'
                    height='28'
                    viewBox='0 0 28 28'
                    fill='none'
                  >
                    <path
                      d='M14.0003 25.6668C20.4437 25.6668 25.667 20.4435 25.667 14.0002C25.667 7.55684 20.4437 2.3335 14.0003 2.3335C7.55702
                  2.3335 2.33368 7.55684 2.33368 14.0002C2.33368 20.4435 7.55702 25.6668 14.0003 25.6668Z'
                      stroke='white'
                      strokeWidth='2.50546'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                    <path
                      d='M14.0001 21C17.8661 21 21.0001 17.866 21.0001 14C21.0001 10.134 17.8661 7 14.0001 7C10.1341 7 7.00009 10.134 7.00009
                  14C7.00009 17.866 10.1341 21 14.0001 21Z'
                      stroke='white'
                      strokeWidth='2.50546'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                    <path
                      d='M14.0001 16.3332C15.2887 16.3332 16.3334 15.2885 16.3334 13.9998C16.3334 12.7112 15.2887 11.6665 14.0001 11.6665C12.7114
                  11.6665 11.6667 12.7112 11.6667 13.9998C11.6667 15.2885 12.7114 16.3332 14.0001 16.3332Z'
                      stroke='white'
                      strokeWidth='2.50546'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </span>

                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                  }}
                >
                  <span
                    style={{
                      color: '#FFFFFF',
                      fontWeight: 600,
                      fontSize: 18,
                    }}
                  >
                    Adaptive Quizzes
                  </span>
                  <span
                    style={{
                      color: '#FFFFFFCC',
                      fontSize: 16,
                      lineHeight: '1.5',
                    }}
                  >
                    Generate assessments that ensure true comprehension, not
                    <br />
                    just completion
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Powered by Assistiv bottom footer */}
          <div
            style={{
              position: 'absolute',
              left: 32,
              right: 32,
              bottom: 33,
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='22'
              height='22'
              viewBox='0 0 121 121'
              fill='none'
            >
              <g filter='url(#filter_powered_assistiv_logo)'>
                <path
                  d='M58.1504 27.4057C59.1415 25.6892 61.619 25.6892 62.61 27.4057C62.61 27.4057 67.6434 36.1237 71.6831 43.1207C75.0642 48.977
                62.5263 85.6993 28.9543 85.6993C26.9723 85.6993 25.7335 83.5537 26.7245 81.8372L58.1504 27.4057Z'
                  fill='white'
                />
                <path
                  d='M94.0361 81.8372C95.0271 83.5537 93.7884 85.6994 91.8064 85.6994H62.8757C61.9246 85.6994 61.2089 84.8305 61.4224 83.9037C63.8967
                73.1625 69.4785 63.6117 77.1688 56.2517C77.8619 55.5884 78.9851 55.7684 79.4648 56.5992L94.0361 81.8372Z'
                  fill='white'
                />
              </g>
              <defs>
                <filter
                  id='filter_powered_assistiv_logo'
                  x='20.1884'
                  y='19.9311'
                  width='82.1515'
                  height='73.7235'
                  filterUnits='userSpaceOnUse'
                  colorInterpolationFilters='sRGB'
                >
                  <feFlood floodOpacity='0' result='BackgroundImageFix' />
                  <feColorMatrix
                    in='SourceAlpha'
                    type='matrix'
                    values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
                    result='hardAlpha'
                  />
                  <feOffset dx='0.883896' dy='0.883896' />
                  <feGaussianBlur stdDeviation='3.53559' />
                  <feComposite in2='hardAlpha' operator='out' />
                  <feColorMatrix type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.6 0' />
                  <feBlend
                    mode='normal'
                    in2='BackgroundImageFix'
                    result='effect1_dropShadow_powered_assistiv_logo'
                  />
                  <feBlend
                    mode='normal'
                    in='SourceGraphic'
                    in2='effect1_dropShadow_powered_assistiv_logo'
                    result='shape'
                  />
                </filter>
              </defs>
            </svg>

            <span
              style={{
                color: '#F5F5F5',
                fontSize: 14,
              }}
            >
              Powered by Assistiv
            </span>
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div
        className='auth-right-panel'
        style={{
          flex: 1,
          height: '100%',
          backgroundColor: '#FFFFFF',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {/* Inner content wrapper */}
        <div
          style={{
            display: 'flex',
            width: '100%',
            maxWidth: '480px',
            padding: '54px 24px',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 16,
            flexShrink: 0,
          }}
        >
          {/* Logo for both mobile and desktop */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
            }}
          >
            {renderLoginLogo()}
          </div>

          {/* Welcome back title */}
          <h1
            style={{
              fontSize: 28,
              fontWeight: 700,
              lineHeight: '1.2',
              textAlign: 'center',
              color: '#101828',
            }}
          >
            Welcome back!
          </h1>
          <p
            style={{
              fontSize: 14,
              color: '#475467',
              textAlign: 'center',
            }}
          >
            Sign in to continue your {brandName} learning journey
          </p>

          {/* Buttons */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              alignSelf: 'stretch',
            }}
          >
            <GoogleLoginButton isAdmin={true} />
            <MicrosoftLoginButton isAdmin={true} />
          </div>

          {/* "or" divider */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              width: '100%',
            }}
          >
            <div
              style={{
                flex: 1,
                height: 1,
                backgroundColor: '#D9D9D9',
              }}
            />
            <span
              style={{
                color: '#757575',
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              or
            </span>
            <div
              style={{
                flex: 1,
                height: 1,
                backgroundColor: '#D9D9D9',
              }}
            />
          </div>

          {/* Email sign-up box */}
          <div
            style={{
              alignSelf: 'stretch',
              borderRadius: 21,
              border: '2px solid #53B1FD',
              background: '#D1E9FF',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 12,
              padding: '12px 16px',
            }}
          >
            <div style={{ marginTop: 2, flexShrink: 0 }}>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='20'
                height='20'
                viewBox='0 0 20 20'
                fill='none'
              >
                <path
                  d='M18.3334 4.99992C18.3334 4.08325 17.5834 3.33325 16.6667 3.33325H3.33341C2.41675 3.33325 1.66675
                  4.08325 1.66675 4.99992M18.3334 4.99992V14.9999C18.3334 15.9166 17.5834 16.6666 16.6667 16.6666H3.33341C2.41675
                  16.6666 1.66675 15.9166 1.66675 14.9999V4.99992M18.3334 4.99992L10.0001 10.8333L1.66675 4.99992'
                  stroke='#1570EF'
                  strokeWidth='1.66667'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <p style={{ fontWeight: 600, fontSize: 14, color: '#1E1E1E' }}>
                Email sign-up coming soon
              </p>
              <p style={{ fontSize: 13, color: '#475467' }}>
                Use your organization&apos;s SSO to sign in for now
              </p>
            </div>
          </div>

          {/* Terms of Service and Privacy Policy */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 22,
              margin: 0,
            }}
          >
            <span
              className='underline cursor-pointer'
              style={{ color: '#1570EF', fontSize: 12 }}
              onClick={() => openModal(true)}
            >
              Terms of Service
            </span>
            <span style={{ color: '#475467', fontSize: 12 }}>·</span>
            <span
              className='underline cursor-pointer'
              style={{ color: '#1570EF', fontSize: 12 }}
              onClick={() => openModal(false)}
            >
              Privacy Policy
            </span>
          </div>
        </div>

        {/* Need help */}
        <div
          style={{ fontSize: 12, color: '#475467', textAlign: 'center', marginTop: -40 }}
          className='text-center'
        >
          Need help? Contact{' '}
          <a
            href='mailto:support@ftfplatforms.com'
            className='underline'
            style={{ color: '#1570EF' }}
          >
            FTF Support
          </a>
          .
        </div>
      </div>
      <style>{`
      .auth-mobile-background {
      display: none;
      }

      @media (max-width: 1199px) {
      html,
      body {
      background: ${authPanelBackground} !important;
       overflow: hidden !important;
       }

       .auth-page {
          position: fixed !important;
          inset: 0 !important;
          width: 100vw !important;
          min-width: 100vw !important;
          height: 100vh !important;
          height: 100dvh !important;
        background: ${authPanelBackground} !important;
          justify-content: center !important;
          padding: 0 !important;
          box-sizing: border-box !important;
          overflow: hidden !important;
       }

          .auth-left-panel {
            display: none !important;
          }

          .auth-mobile-background {
            display: block;
            position: fixed;
            top: -4px;
            right: -4px;
            bottom: -4px;
            left: -4px;
            overflow: hidden;
            border-radius: 0;
            background: ${authPanelBackground};
          }

          .auth-mobile-wave {
            position: absolute;
            inset: 0;
            background-image: url('/login/wave.png');
            background-position: center center;
            background-size: cover;
            background-repeat: no-repeat;
            opacity: 1;
            mix-blend-mode: hard-light;
            pointer-events: none;
          }

            .auth-right-panel {
            position: relative !important;
            z-index: 1 !important;
            flex: 0 1 auto !important;
            width: min(560px, calc(100% - 32px)) !important;
            min-height: min(580px, calc(100vh - 96px)) !important;
            height: auto !important;
            padding-bottom: 18px !important;
            box-sizing: border-box !important;
            overflow: visible !important;

            border-radius: 32px !important;
            background: rgba(255, 255, 255, 0.96) !important;
            box-shadow: 0 24px 80px rgba(16, 24, 40, 0.18) !important;
            backdrop-filter: blur(10px);
          }
        }

        @media (hover: none) and (pointer: coarse) and (min-width: 768px) and (max-width: 1199px) {
          .auth-page {
            padding: 0 !important;
          }

          .auth-mobile-background {
            top: -4px;
            right: -4px;
            bottom: -4px;
            left: -4px;
            border-radius: 0;
          }

          .auth-right-panel {
            width: min(640px, calc(100% - 96px)) !important;
            max-height: calc(100vh - 128px) !important;
          }
        }
      `}</style>

      <TextModal
        modalName='TermsPolicy'
        title={modalData.title}
        version={modalData.version}
        intro={modalData.intro}
        sections={modalData.sections}
        displayText={modalData.content}
      />
    </div>
  );
}
